import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDbData, setUserDbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Auth functions
  const signup = async (email, password, username) => {
    try {
      setAuthError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        username: username,
        usernameLower: username.toLowerCase(),
        displayName: username,
        createdAt: new Date().toISOString(),
        followers: [],
        following: [],
        bio: '',
        photoURL: '',
        online: true
      });
      
      // Send email verification
      await sendEmailVerification(user);
      
      return user;
    } catch (error) {
      console.error('Error in signup:', error);
      
      // Map Firebase error codes to user-friendly messages
      let errorMessage;
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Email address is already in use';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak. It should be at least 6 characters';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          errorMessage = error.message || 'Failed to create account';
      }
      
      setAuthError(errorMessage);
      throw { ...error, message: errorMessage }; // Throw modified error with user-friendly message
    }
  };
  
  const login = async (email, password) => {
    try {
      setAuthError(null);
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error in login:', error);
      
      // Map Firebase error codes to user-friendly messages
      let errorMessage;
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed login attempts. Please try again later';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          errorMessage = error.message || 'Failed to sign in';
      }
      
      setAuthError(errorMessage);
      throw { ...error, message: errorMessage }; // Throw modified error with user-friendly message
    }
  };
  
  const logout = async () => {
    try {
      setAuthError(null);
      return await signOut(auth);
    } catch (error) {
      console.error('Error in logout:', error);
      setAuthError(error.message || 'Failed to sign out');
      throw error;
    }
  };
  
  const resetPassword = async (email) => {
    try {
      setAuthError(null);
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error in resetPassword:', error);
      setAuthError(error.message || 'Failed to send password reset');
      throw error;
    }
  };
  
  // Fetch user data from Firestore
  const fetchUserData = async (userId) => {
    try {
      if (!userId) return null;
      
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data();
      } else {
        console.log("No user document found!");
        return null;
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  };
  
  // Listen to auth state changes
  useEffect(() => {
    setLoading(true);

    try {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        try {
          setCurrentUser(user);
          
          if (user) {
            const userData = await fetchUserData(user.uid);
            setUserDbData(userData);
          } else {
            setUserDbData(null);
          }
        } catch (error) {
          console.error("Error in auth state listener:", error);
          setAuthError(error.message || 'Authentication error');
        } finally {
          setLoading(false);
          setAuthInitialized(true);
        }
      });
      
      return unsubscribe;
    } catch (error) {
      console.error("Critical auth setup error:", error);
      setAuthError(error.message || 'Failed to initialize authentication');
      setLoading(false);
      setAuthInitialized(true);
      return () => {};
    }
  }, []);
  
  const value = {
    currentUser,
    userDbData,
    loading,
    authInitialized,
    authError,
    signup,
    login,
    logout,
    resetPassword
  };
  
  // Only render children when Firebase Auth is initialized
  return (
    <AuthContext.Provider value={value}>
      {!authInitialized ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}; 